import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
} from "@mui/material";
import { describeError, useToast } from "./toast";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import { foyerApi, type BulkActionResult } from "../http/foyer";

export type BulkAction = "reboot" | "message";

interface Props {
  open: boolean;
  premiseId: string;
  action: BulkAction;
  /** The hand-picked screens this action targets. */
  screenIds: string[];
  onClose: () => void;
  /** Called with the API result once the action is dispatched. */
  onDone: (result: BulkActionResult, action: BulkAction) => void;
}

/**
 * Confirm + dispatch a Fleet bulk action (reboot / message). Each action is
 * routed server-side through a rollout, so this dialog also offers staged
 * delivery: dispatch a canary slice first, then expand the rest from the
 * Rollouts page. Replaces the old `confirm()` / `prompt()` flows.
 */
export default function BulkActionDialog({
  open,
  premiseId,
  action,
  screenIds,
  onClose,
  onDone,
}: Props) {
  const count = screenIds.length;
  const [body, setBody] = useState("");
  const [stage, setStage] = useState(false);
  const [canaryPct, setCanaryPct] = useState(10);
  const [busy, setBusy] = useState(false);
  const toast = useToast();
  const { t } = useTranslation(["premises", "common"]);

  // Reset the form each time the dialog (re)opens for a fresh selection.
  useEffect(() => {
    if (open) {
      setBody("");
      setStage(false);
      setCanaryPct(10);
      setBusy(false);
    }
  }, [open, action]);

  const isMessage = action === "message";
  const canSubmit = !busy && count > 0 && (!isMessage || body.trim() !== "");

  async function submit() {
    setBusy(true);
    const pct = stage ? Math.max(1, Math.min(100, canaryPct)) : undefined;
    try {
      const result = isMessage
        ? await foyerApi.messageBulk({
            premiseId,
            screenIds,
            body: body.trim(),
            title: "Reception",
            canaryPct: pct,
          })
        : await foyerApi.rebootBulk(premiseId, screenIds, pct);
      onDone(result, action);
      onClose();
    } catch (err) {
      // The dialog stays open with what was typed, so a retry is one click.
      toast.error(
        describeError(
          t,
          isMessage
            ? count === 1
              ? "sendMessageToOneTv"
              : "sendMessageToTvs"
            : count === 1
              ? "rebootOneTv"
              : "rebootTvs",
          err,
          { count }
        )
      );
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>
        {isMessage
          ? t("bulkActionDialog.messageTitle", { count })
          : t("bulkActionDialog.rebootTitle", { count })}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ mt: 0.5 }}>
          {!isMessage && (
            <DialogContentText>
              {t("bulkActionDialog.rebootBody", { count })}
            </DialogContentText>
          )}
          {isMessage && (
            <TextField
              label={t("bulkActionDialog.message")}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder={t("bulkActionDialog.messagePlaceholder")}
              multiline
              minRows={2}
              fullWidth
              autoFocus
              data-testid="bulk-message-body"
            />
          )}

          <FormControlLabel
            control={
              <Switch
                checked={stage}
                onChange={(e) => setStage(e.target.checked)}
                data-testid="bulk-stage-toggle"
              />
            }
            label={t("bulkActionDialog.stage")}
          />
          {stage && (
            <Stack spacing={1}>
              <TextField
                label={t("bulkActionDialog.canaryPct")}
                type="number"
                size="small"
                value={canaryPct}
                onChange={(e) => setCanaryPct(Number(e.target.value) || 0)}
                inputProps={{
                  min: 1,
                  max: 100,
                  "data-testid": "bulk-canary-pct",
                }}
                sx={{ maxWidth: 140 }}
              />
              <DialogContentText sx={{ typography: "bodySm" }}>
                {t("bulkActionDialog.canaryHint", { pct: canaryPct })}
              </DialogContentText>
            </Stack>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          {t("common:buttons.cancel")}
        </Button>
        <Button
          variant="contained"
          onClick={submit}
          disabled={!canSubmit}
          data-testid="bulk-action-confirm"
        >
          {stage
            ? t("bulkActionDialog.startCanary")
            : isMessage
              ? t("bulkActionDialog.send")
              : t("bulkActionDialog.reboot")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
